import { BigNumber, ethers } from 'ethers'
import {
  BSC_CHAIN_ID_NUMBER,
  BASE_CHAIN_ID_NUMBER,
  ETH_CHAIN_ID_NUMBER,
  MATIC_CHAIN_ID_NUMBER,
} from '../constants'

const stable: Record<number, number> = {
  [ETH_CHAIN_ID_NUMBER]: 18,
  [BASE_CHAIN_ID_NUMBER]: 18,
}
if (!Number.isNaN(BSC_CHAIN_ID_NUMBER)) {
  stable[BSC_CHAIN_ID_NUMBER] = 18
}
if (!Number.isNaN(MATIC_CHAIN_ID_NUMBER)) {
  stable[MATIC_CHAIN_ID_NUMBER] = 6
}

export const stableCoinDecimals = stable

export const TokenBNtoNumber = (number: BigNumber, chainId?: number) => {
  if (!number) return 0

  return parseFloat(ethers.utils.formatUnits(number, chainId ? stableCoinDecimals[chainId] : undefined))
}

export const BNToNumber = (number: BigNumber, decimals = 18) => {
  return number ? parseFloat(ethers.utils.formatUnits(number, decimals)) : 0
}

export const TokenBNtoString = (chainId?: number, number?: BigNumber) => {
  if (!number) return '0'
  const formattedNumber = ethers.utils.formatUnits(number, chainId ? stableCoinDecimals[chainId] : 18)

  return formattedNumber
}

export const BNToEthers = (bigNumber: BigNumber) => {
  return bigNumber.mul(BigNumber.from(10).pow(BigNumber.from(18)))
}

export const BNToString = (value?: BigNumber) => {
  return value ? value.toString() : ''
}

export const stringToBigNumber = (string: string, decimals = 18): BigNumber => {
  let decimalPlaces = BigNumber.from(0)
  let integer = BigNumber.from(0)

  if (string.includes('.') || string.includes(',')) {
    const splittedNumber = string.includes('.') ? string.split('.') : string.split(',')
    const splittedInteger = splittedNumber[0]
    const splittedDecimal = splittedNumber[1] || '0'

    decimalPlaces = BigNumber.from(splittedDecimal.length)

    const integerPart = splittedInteger.length > 0 ? splittedInteger : '0'
    const completeNumber = splittedDecimal.length > 0 ? integerPart + splittedDecimal : integerPart

    integer = BigNumber.from(completeNumber)
  } else {
    integer = BigNumber.from(string || '0')
  }

  return decimalPlaces.lte(BigNumber.from(decimals))
    ? integer.mul(BigNumber.from(10).pow(BigNumber.from(decimals).sub(decimalPlaces)))
    : BigNumber.from(-1)
}

export const BNtoFormattedString = (number: BigNumber, chainId: number): string => {
  if (!number) return '0'

  const formattedNumber = ethers.utils.formatUnits(number, chainId ? stableCoinDecimals[chainId] : 18)
  return formattedNumber
}

// Function to convert wei (in string format) to a  string
export const weiToEther = (weiString: string, decimals: number): string => {
  if (!weiString) return '0'

  // Convert the string to a BigNumber for accuracy in calculations
  const weiAmount = ethers.BigNumber.from(weiString)

  // Use ethers to convert wei to ether
  const etherAmount = ethers.utils.formatUnits(weiAmount, decimals)

  // Split the number into integer and decimal parts
  const [integerPart, decimalPart] = etherAmount.split('.')

  // If there is no decimal part, return integer
  if (!decimalPart) {
    return integerPart
  }
  // If there is a decimal part, remove trailing zeros
  const trimmedDecimalPart = decimalPart.padEnd(11, '0').slice(0, 11).replace(/0+$/, '')

  if (trimmedDecimalPart === '') {
    return integerPart
  }

  return `${integerPart}.${trimmedDecimalPart}`
}

export const etherToWeiString = (etherString: string, decimals = 18): string => {
  if (!etherString) return '0'
  // Truncate the decimal part to a maximum of 18 characters to ensure the result has at most 18 decimal places
  const parts = etherString.split('.')
  const integerPart = parts[0]
  let decimalPart = parts.length > 1 ? parts[1] : ''
  decimalPart = decimalPart.slice(0, decimals) // Keep only the first 18 decimal places

  // Recombine the integer and truncated decimal parts
  const truncatedEtherString = `${integerPart}.${decimalPart}`
  // Convert the truncated ether string to a BigNumber in wei
  const weiBigNumber = ethers.utils.parseUnits(truncatedEtherString, decimals)
  // Convert the BigNumber to a string
  const weiString = weiBigNumber.toString()
  return weiString
}

export const BNToNumberWithDecimals = (number: BigNumber, decimals = 18, maxDecimals = 6) => {
  if (!number) return 0
  const formattedNumber = parseFloat(ethers.utils.formatUnits(number, decimals))
  const multiplier = 10 ** maxDecimals
  const preciseNumber = Math.floor(formattedNumber * multiplier) / multiplier
  return preciseNumber
}
